import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { usePostLogsSearch } from '../../../api/logs/logs';
import { ErrorInfo, LogSearchConditions } from '../../../api/model';
import { useDefaultOptions } from '../../../hooks/api';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import { LogList } from './LogList';
import { LogSearch } from './LogSearch';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|ログ一覧')}</title>
    </Helmet>
  );
}

function LogPage() {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 検索条件部分が開いているかどうか
  const [openSearchConditions, setOpenSearchConditions] = useState(true);

  // 二週間前の日付取得
  const getDay = () => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 14);
    const y = String(dt.getFullYear());
    const m = `00${dt.getMonth() + 1}`.slice(-2);
    const d = `00${dt.getDate()}`.slice(-2);
    return `${y}-${m}-${d}`;
  };

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<LogSearchConditions>({ log_at_from: getDay(), log_at_to: '' });

  // 検索条件
  const refConditions = useRef(sessionStorageUtils.get<LogSearchConditions>('Logs.SearchConditions', {}));

  // 検索 API 情報
  const {
    data,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostLogsSearch<AxiosError<ErrorInfo>, unknown>(options);

  // 一覧の初回表示
  useEffect(() => {
    const postUsers = async () => {
      await searchAsync({ data: defaultConditions });
    };
    void postUsers();
  }, [defaultConditions, searchAsync]);

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: LogSearchConditions) => {
      const postLogs = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          sessionStorageUtils.set('Logs.SearchConditions', next);
          refConditions.current = next;
        };

        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postLogs();
    },
    [searchAsync]
  );

  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('ログ検索')}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearchConditions((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearchConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>

        <LogSearch
          openSearchConditions={openSearchConditions}
          defaultConditions={defaultConditions}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
        />
        <LogList data={data} isLoading={isLoading} isError={isError} error={error} />
      </section>
    </div>
  );
}

export default LogPage;
